/**
 * Preparo das fotos do checklist antes de enviar.
 *
 * A foto que sai da câmera de um celular tem 3–5 MB. Mandar isso direto (uma por
 * item do checklist) estouraria o limite do servidor e faria o envio demorar
 * minutos numa rede de campo. Aqui a imagem é redesenhada num canvas com o lado
 * maior limitado e recomprimida em JPEG — o resultado fica na casa de 150–300 KB,
 * de sobra para conferir um pneu, um farol ou uma avaria.
 */

/** Lado maior da imagem depois de reduzida (px). */
const LADO_MAXIMO = 1280;
/** Qualidade do JPEG (0–1). 0.72 mantém legível sem inchar o arquivo. */
const QUALIDADE = 0.72;
/** Teto de segurança: o servidor recusa acima de ~1 MB de imagem. */
const MAX_BASE64 = 1_300_000;

export class FotoInvalida extends Error {}

/** Lê o arquivo como data URL. */
function lerComoDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result ?? ""));
    fr.onerror = () => reject(new FotoInvalida("Não foi possível ler a imagem."));
    fr.readAsDataURL(file);
  });
}

/** Carrega a data URL num elemento de imagem (para desenhar no canvas). */
function carregarImagem(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new FotoInvalida("Arquivo de imagem inválido."));
    img.src = dataUrl;
  });
}

/**
 * Recebe o arquivo escolhido na câmera/galeria e devolve uma data URL JPEG
 * reduzida, pronta para gravar. Lança `FotoInvalida` com mensagem em português
 * quando o arquivo não serve.
 */
export async function prepararFoto(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new FotoInvalida(`"${file.name}" não é uma imagem.`);
  }

  const original = await lerComoDataUrl(file);
  const img = await carregarImagem(original);

  const maiorLado = Math.max(img.width, img.height);
  const escala = maiorLado > LADO_MAXIMO ? LADO_MAXIMO / maiorLado : 1;
  const largura = Math.max(1, Math.round(img.width * escala));
  const altura = Math.max(1, Math.round(img.height * escala));

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new FotoInvalida("O aparelho não conseguiu processar a imagem.");
  // Fundo branco: JPEG não tem transparência e um PNG transparente viraria preto.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, largura, altura);
  ctx.drawImage(img, 0, 0, largura, altura);

  let saida = canvas.toDataURL("image/jpeg", QUALIDADE);
  // Foto muito detalhada ainda passando do teto: reduz a qualidade em degraus.
  for (let q = QUALIDADE - 0.15; saida.length > MAX_BASE64 && q >= 0.3; q -= 0.15) {
    saida = canvas.toDataURL("image/jpeg", q);
  }
  if (saida.length > MAX_BASE64) {
    throw new FotoInvalida("A imagem ficou grande demais. Tire a foto de novo, mais de perto.");
  }
  return saida;
}
